/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import {
  cpfOrCnpjMask,
  onlyNumbers,
  phoneMask,
} from '../../../utils/normalize';
import { InputText } from '../../../components/InputText';
import { Button } from '../../../components/Button';
import { Label } from '../../../components/Label';
import { toastr } from 'react-redux-toastr';
import UserRepository from '../../../repositories/UserRepository';
import { isCPF, isCPFOrCNPJ, isPhone } from 'brazilian-values';
import { isMobile, validadeEmail } from '../../../utils/validation';
import TermsOfUseModal from '../../confirmationData/components/TermsOfUseModal/TermsOfUseModal';
import { termsOfUseRepository } from '../../../repositories/termsOfUseRepository';
import { ErrorText } from '../../../components/InputText/styles';

const PersonalData = ({
  history,
  values,
  errors,
  setLoading,
  loading,
  handleChange,
  trigger,
  nextPage,
  setErrors,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [termsOfUse, setTermsOfUse] = useState('');
  const validation = async () => {
    try {
      setLoading(true);
      const { email, cpfCnpj, phone } = values;
      const newErrors = {};

      if (!isCPFOrCNPJ(onlyNumbers(cpfCnpj))) {
        newErrors.cpfCnpj = 'CPF/CNPJ inválido.';
        setErrors({ ...errors, ...newErrors });
        return false;
      }

      if (!validadeEmail(email)) {
        newErrors.email = 'E-mail inválido.';
        setErrors({ ...errors, ...newErrors });
        return false;
      }

      if (!isPhone(onlyNumbers(phone))) {
        newErrors.phone = 'Telefone inválido.';
        setErrors({ ...errors, ...newErrors });
        return false;
      }

      const haveCpfCnpj = await UserRepository.verifyCpfCnpj(
        onlyNumbers(cpfCnpj),
      );
      if (haveCpfCnpj) {
        newErrors.cpfCnpj = (
          <>
            O {isCPF(cpfCnpj) ? 'CPF' : 'CNPJ'} está vinculado em outra conta.
            Em caso de dúvidas,
            {isMobile() ? null : <br />}
            entre em contato com{' '}
            <a href="mailto:ouvidoria@sobrerodas.club">
              ouvidoria@sobrerodas.club
            </a>
          </>
        );
        setErrors({ ...errors, ...newErrors });
        toastr.warning(
          `Já existe uma conta com esse ${isCPF(cpfCnpj) ? 'CPF' : 'CNPJ'}`,
          `O ${isCPF(cpfCnpj) ? 'CPF' : 'CNPJ'} está vinculado em outra conta. Em caso de dúvidas, entre em contato com ouvidoria@sobrerodas.club`,
        );
        return false;
      }

      const haveEmail = await UserRepository.verifyEmail(email);
      if (haveEmail) {
        newErrors.email = (
          <>
            O email está vinculado em outra conta, clique abaixo para logar
            <br />
            ou{' '}
            <a
              href="#"
              onClick={() =>
                history.push({
                  pathname: '/client/login',
                  state: { forgotPassword: true },
                })
              }
            >
              clique aqui para recuperar a senha
            </a>
          </>
        );
        setErrors({ ...errors, ...newErrors });
        toastr.warning(
          `Já existe uma conta com esse e-mail`,
          'O e-mail está vinculado em outra conta. Em caso de dúvidas, entre em contato com ouvidoria@sobrerodas.club',
        );
        return false;
      }

      const havePhone = await UserRepository.verifyPhone(onlyNumbers(phone));
      if (havePhone) {
        newErrors.phone = (
          <>
            O telefone está vinculado em outra conta, clique abaixo para logar
            <br />
            ou{' '}
            <a
              href="#"
              onClick={() =>
                history.push({
                  pathname: '/client/login',
                  state: { forgotPassword: true },
                })
              }
            >
              clique aqui para recuperar a senha
            </a>
          </>
        );
        setErrors({ ...errors, ...newErrors });
        toastr.warning(
          `Já existe uma conta com esse telefone`,
          'O telefone está vinculado em outra conta. Em caso de dúvidas, entre em contato com ouvidoria@sobrerodas.club',
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error('Validation error:', error);
      toastr.error(
        'Erro',
        error?.response?.data?.message || 'Erro ao validar os dados.',
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = async () => {
    const isValidFields = await trigger([
      'name',
      'cpfCnpj',
      'email',
      'phone',
      'password',
      'confirmPassword',
      'termsAccepted',
    ]);

    if (!isValidFields) {
      toastr.error('Erro', 'Por favor, corrija os erros antes de prosseguir.');
      return;
    }

    const isValid = await validation();
    if (isValid) {
      nextPage();
    }
  };

  const handleAcceptTerms = () => {
    handleChange('termsAccepted', true);
    setIsModalOpen(false);
  };

  useEffect(() => {
    const getTermsOfUse = async () => {
      const response = await termsOfUseRepository.getTermsOfUse();
      setTermsOfUse(response.data);
      handleChange('termId', response.data.id);
    };
    if (!termsOfUse) {
      getTermsOfUse();
    }
  }, [handleChange, termsOfUse]);

  const canAdvance =
    values.name &&
    values.cpfCnpj &&
    values.email &&
    values.phone &&
    values.password &&
    values.confirmPassword &&
    values.termsAccepted;

  return (
    <>
      <header className="auth-form-header">
        <h2>Crie sua conta</h2>
        <p>Em poucos passos o seu veículo entra na sua garagem.</p>
      </header>
      <div className="signup-fields">
        <Label required htmlFor="signup-name">Nome completo</Label>
        <InputText
          id="signup-name"
          name="name"
          autoComplete="name"
          value={values.name}
          onChange={(e) => handleChange('name', e.target.value)}
          onBlur={() => trigger(['name'])}
          maxLength={50}
          isLoading={loading}
          error={errors.name}
        />

        <Label required htmlFor="signup-cpf-cnpj">CPF/CNPJ</Label>
        <InputText
          id="signup-cpf-cnpj"
          name="cpfCnpj"
          inputMode="numeric"
          value={values.cpfCnpj}
          onChange={(e) =>
            handleChange('cpfCnpj', cpfOrCnpjMask(e.target.value))
          }
          onBlur={() => trigger(['cpfCnpj'])}
          maxLength={18}
          isLoading={loading}
          error={errors.cpfCnpj}
        />

        <Label required htmlFor="signup-email">E-mail</Label>
        <InputText
          id="signup-email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="seu@email.com"
          value={values.email}
          onChange={(e) => handleChange('email', e.target.value)}
          onBlur={() => trigger(['email'])}
          isLoading={loading}
          error={errors.email}
        />

        <Label required htmlFor="signup-phone">Celular</Label>
        <InputText
          id="signup-phone"
          name="phone"
          type="tel"
          autoComplete="tel-national"
          placeholder="(00) 00000-0000"
          value={values.phone}
          onChange={(e) => handleChange('phone', phoneMask(e.target.value))}
          onBlur={() => trigger(['phone'])}
          isLoading={loading}
          error={errors.phone}
        />

        <div className="signup-label-row">
          <Label required htmlFor="signup-password">Senha</Label>
          <span className="signup-hint">Mínimo de 6 caracteres</span>
        </div>
        <InputText
          id="signup-password"
          name="password"
          autoComplete="new-password"
          value={values.password}
          onChange={(e) => handleChange('password', e.target.value)}
          onBlur={() => trigger(['password'])}
          type="password"
          isLoading={loading}
          error={errors.password}
        />

        <Label required htmlFor="signup-confirm-password">Confirme a senha</Label>
        <InputText
          id="signup-confirm-password"
          name="confirmPassword"
          autoComplete="new-password"
          value={values.confirmPassword}
          onChange={(e) => handleChange('confirmPassword', e.target.value)}
          onBlur={() => trigger(['confirmPassword'])}
          type="password"
          isLoading={loading}
          error={errors.confirmPassword}
        />

        <div className="signup-terms">
          <input
            id="signup-terms"
            type="checkbox"
            checked={values.termsAccepted}
            onChange={(e) => handleChange('termsAccepted', e.target.checked)}
          />
          <label htmlFor="signup-terms">
            Declaro que li e aceito os{' '}
            <button
              type="button"
              className="auth-link"
              onClick={() => setIsModalOpen(true)}
            >
              termos de uso
            </button>
          </label>
        </div>
        {errors.termsAccepted && <ErrorText>{errors.termsAccepted}</ErrorText>}

        <Button
          color="green450"
          type="button"
          className="auth-submit"
          onClick={handleNextPage}
          loading={loading}
          disabled={!canAdvance || loading}
        >
          Avançar
        </Button>
        <p className="auth-signup">
          Já possui conta?{' '}
          <button
            type="button"
            className="auth-link"
            onClick={() => history.push('/client/login')}
          >
            Entrar
          </button>
        </p>
      </div>
      <TermsOfUseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAccept={handleAcceptTerms}
        children={termsOfUse.description}
        initialChecked={values.termsAccepted}
      />
    </>
  );
};

export default withRouter(PersonalData);
